import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import * as S from '@/components/UI/Dropdown/SelectDropdownMenu.styles';
import { PropertyOption } from '@/api/types/categories.types';
import { Collection } from '@/components/AdForm/AdForm.types';
import { NotFound } from '@/components/NotFoundPlaceholder/NotFoundPlaceholder.styles';

interface Props {
  isOpen: boolean;
  query?: string;
  options?: PropertyOption[];
  collection?: Collection;
  popularOptions?: PropertyOption[];
  onItemClick?: (option: PropertyOption) => void;
}

export const CollectionFormSelectDropdownMenu: FC<Props> = ({
  isOpen,
  query = '',
  options,
  collection,
  popularOptions,
  onItemClick,
}) => {
  const { t } = useTranslation();
  const list =
    options?.filter(option =>
      option.name.toLowerCase().includes(query.toLowerCase())
    ) || [];

  function onOptionClick(value: PropertyOption) {
    if (onItemClick) {
      onItemClick(value);
    }
  }

  function renderOption(option: PropertyOption) {
    const { legacyResourceId, propertyLegacyId } = option;

    const isChecked =
      legacyResourceId.toString() ===
      (collection?.[propertyLegacyId] as PropertyOption)?.legacyResourceId;

    return (
      <S.SelectDropdownMenuItem
        key={option.name}
        onChangeHook={() => onOptionClick(option)}
        value={isChecked}
        label={option.name}
        appearance="radio"
        size="sm"
        highlightLabel
        hasBorderBottom={false}
        dataTestId={`property-option-${option.legacyResourceId}`}
      />
    );
  }

  const withPopularOptions = !!popularOptions?.length && !query;

  return (
    isOpen && (
      <S.SelectDropdownMenu>
        {withPopularOptions && (
          <>
            <S.SelectDropdownMenuItemsTitle>
              {t('common.popular')}
            </S.SelectDropdownMenuItemsTitle>

            <S.SelectDropdownMenuContainer>
              {popularOptions.map(renderOption)}
            </S.SelectDropdownMenuContainer>

            <S.SelectDropdownMenuItemsTitle>
              {t('common.all')}
            </S.SelectDropdownMenuItemsTitle>
          </>
        )}

        <S.SelectDropdownMenuContainer $hasYPadding>
          {list.length > 0 ? (
            list.map(renderOption)
          ) : (
            <NotFound>{t('common.nothing_found')}</NotFound>
          )}
        </S.SelectDropdownMenuContainer>
      </S.SelectDropdownMenu>
    )
  );
};

CollectionFormSelectDropdownMenu.displayName = `CollectionFormSelectDropdownMenu`;
